import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { debounce } from "lodash";
import { countTeamsForUserAndEvent, getUserByEmail } from "config/airtable";
import {
  validateAndCapitalizeFirstName,
  validateAndCapitalizeLastName,
  validatePhoneNumber,
} from "utils/validations.utils";
import { formatPhoneNumber } from "utils/helper.utils";

const ContactInformation = ({
  userInfoFields,
  setUserInfoFields,
  updateUserInfoField,
  setTeamCount,
  eventID,
}) => {
  const [loading, setLoading] = useState(false);
  const [showFields, setShowFields] = useState(false);

  // Show the rest once any primary contact data exists
  useEffect(() => {
    const { email, first_name, last_name, phone_number } = userInfoFields;
    if (email.data || first_name.data || last_name.data || phone_number.data) {
      setShowFields(true);
    }
  }, [userInfoFields]);

  // Debounced email lookup
  const debouncedCheckEmail = debounce(async (emailValue) => {
    // Basic format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      setUserInfoFields((prev) => ({
        ...prev,
        email: { ...prev.email, error: "Please enter a valid email address." },
      }));
      setLoading(false);
      return;
    }

    // Clear previous error and store
    setUserInfoFields((prev) => ({
      ...prev,
      email: { data: emailValue, error: "" },
    }));
    localStorage.setItem("email", emailValue);

    try {
      const airtableUser = await getUserByEmail(emailValue);
      if (airtableUser) {
        // populate existing user data
        setUserInfoFields((prev) => ({
          ...prev,
          id: airtableUser.id,
          email: { data: airtableUser.data.email, error: "" },
          first_name: { data: airtableUser.data.first_name, error: "" },
          last_name: { data: airtableUser.data.last_name, error: "" },
          phone_number: { data: airtableUser.data.phone_number, error: "" },
        }));
        // fetch updated team count
        const count = await countTeamsForUserAndEvent(airtableUser.id, eventID);
        setTeamCount(count);
      } else {
        // new user, clear other fields
        setUserInfoFields((prev) => ({
          ...prev,
          id: null,
          first_name: { data: "", error: "" },
          last_name: { data: "", error: "" },
          phone_number: { data: "", error: "" },
        }));
      }
    } catch (error) {
      console.error("Error fetching user by email:", error);
    } finally {
      setLoading(false);
    }
  }, 1000);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setLoading(true);
    setUserInfoFields((prev) => ({
      ...prev,
      email: { data: value, error: "" },
    }));
    debouncedCheckEmail(value);
  };

  return (
    <Box sx={{ my: 3 }}>
      <Typography
        variant="h6"
        fontWeight="bold"
        gutterBottom
        mb={2}
        color="secondary"
      >
        Contact Information
      </Typography>

      <Grid container spacing={2}>
        {/* Email Field */}
        <Grid size={12}>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            value={userInfoFields.email.data || ""}
            error={!!userInfoFields.email.error}
            helperText={userInfoFields.email.error}
            onChange={handleEmailChange}
            InputProps={{
              endAdornment:
                loading && userInfoFields.email.data ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>

        {showFields && (
          <>
            {/* First Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={userInfoFields.first_name.data || ""}
                onChange={(e) =>
                  updateUserInfoField("first_name", "data", e.target.value)
                }
                onBlur={(e) => {
                  const { error, data } = validateAndCapitalizeFirstName(
                    e.target.value
                  );
                  setUserInfoFields((prev) => ({
                    ...prev,
                    first_name: { data, error },
                  }));
                }}
                error={!!userInfoFields.first_name.error}
                helperText={userInfoFields.first_name.error}
              />
            </Grid>

            {/* Last Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Last Name"
                fullWidth
                required
                value={userInfoFields.last_name.data || ""}
                onChange={(e) =>
                  updateUserInfoField("last_name", "data", e.target.value)
                }
                onBlur={(e) => {
                  const { error, data } = validateAndCapitalizeLastName(
                    e.target.value
                  );
                  setUserInfoFields((prev) => ({
                    ...prev,
                    last_name: { data, error },
                  }));
                }}
                error={!!userInfoFields.last_name.error}
                helperText={userInfoFields.last_name.error}
              />
            </Grid>

            {/* Phone Number */}
            <Grid size={12}>
              <TextField
                label="Phone Number"
                fullWidth
                required
                value={userInfoFields.phone_number.data || ""}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setUserInfoFields((prev) => ({
                    ...prev,
                    phone_number: { data: formatted, error: "" },
                  }));
                }}
                onBlur={(e) => {
                  const error = validatePhoneNumber(e.target.value);
                  setUserInfoFields((prev) => ({
                    ...prev,
                    phone_number: { data: prev.phone_number.data, error },
                  }));
                }}
                error={!!userInfoFields.phone_number.error}
                helperText={userInfoFields.phone_number.error}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default ContactInformation;
